import { useMemo } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridDensity } from '@mui/x-data-grid';
import { buildCompactColumnVisibility } from '../components/ui/gridResponsive';

export function useResponsiveGrid(
  columns: GridColDef[] = [],
  externalVisibility?: GridColumnVisibilityModel,
) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isCompact = isMobile || isTablet;

  const autoVisibility = useMemo(
    () => buildCompactColumnVisibility(columns, isMobile ? 'mobile' : isTablet ? 'tablet' : false),
    [columns, isMobile, isTablet],
  );

  const columnVisibilityModel = useMemo(
    () => ({ ...autoVisibility, ...externalVisibility }),
    [autoVisibility, externalVisibility],
  );

  const density: GridDensity = isMobile ? 'compact' : 'standard';
  const pageSize = isMobile ? 10 : isTablet ? 15 : 25;

  const responsiveColumns = useMemo(() => {
    if (!isCompact) return columns;
    return columns.map((col) => {
      if (col.field === 'actions') {
        return {
          ...col,
          width: isMobile ? 72 : 120,
          minWidth: 72,
          headerName: isMobile ? '' : col.headerName,
        };
      }
      if (typeof col.width === 'number' && col.width > 140) {
        return { ...col, width: isMobile ? Math.min(col.width, 100) : Math.min(col.width, 140) };
      }
      return col;
    });
  }, [columns, isCompact, isMobile]);

  return {
    isMobile,
    isTablet,
    isCompact,
    responsiveColumns,
    columnVisibilityModel,
    density,
    pageSize,
  };
}
